import React, { useState, useEffect } from 'react';
import DashboardLayout from "../layouts/DashboardLayout";
import InfoModal from "../layouts/InfoModal";
import PaginatedTableContainer from "../containers/PaginatedTableContainer";
import {
    getAllMeetings,
    searchMeetings,
    getMemberListOnMeeting,
    getChatListOnMeeting,
    getMeetingImageUrl,
    changeMeetingStatus,
    hardDeleteMeeting
} from "../../api/AdminApi";

const MeetingManagementPage = () => {
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    // 검색 관련 상태
    const [searchQuery, setSearchQuery] = useState('');
    const [searchField, setSearchField] = useState('id');
    const [isSearchMode, setIsSearchMode] = useState(false);

    // 모달 관련 상태
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedMeeting, setSelectedMeeting] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);

    // 탭 관련 상태
    const [activeTab, setActiveTab] = useState('info');

    // 모임원 목록
    const [meetingMembers, setMeetingMembers] = useState([]);

    // 채팅 목록
    const [chatList, setChatList] = useState([]);

    // 이미지 관련 상태
    const [meetingImageUrl, setMeetingImageUrl] = useState(null);
    const [imageLoading, setImageLoading] = useState(false);

    // 상태 변경
    const [selectedStatus, setSelectedStatus] = useState('');

    const statusOptions = [
        { value: 'RECRUITING', label: 'RECRUITING' },
        { value: 'CONFIRMED', label: 'CONFIRMED' },
        { value: 'READY', label: 'READY' },
        { value: 'END', label: 'END' },
    ];

    const fetchMeetings = async (page = 0) => {
        setLoading(true);
        try {
            const response = await getAllMeetings(page, 20);
            setMeetings(response.content);
            setCurrentPage(response.currentPage);
            setTotalPages(response.totalPages);
            setTotalElements(response.totalElements);
            setIsSearchMode(false);
        } catch (error) {
            console.error('모임 목록 조회 실패:', error);
            alert('모임 목록을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (page = 0) => {
        if (!searchQuery.trim()) {
            alert('검색어를 입력해주세요.');
            return;
        }

        setLoading(true);
        try {
            const response = await searchMeetings(page, 20, searchQuery, searchField);
            setMeetings(response.content);
            setCurrentPage(response.currentPage);
            setTotalPages(response.totalPages);
            setTotalElements(response.totalElements);
            setIsSearchMode(true);
        } catch (error) {
            console.error('모임 검색 실패:', error);
            alert('모임 검색에 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = async (meeting) => {
        setModalLoading(true);
        setModalOpen(true);
        setActiveTab('info'); // 기본 탭으로 설정

        try {
            setSelectedMeeting(meeting);
            setSelectedStatus(meeting.status);

            // 모임원 목록 조회
            const membersResponse = await getMemberListOnMeeting(meeting.id);
            setMeetingMembers(membersResponse);
        } catch (error) {
            console.error('모임 상세 조회 실패:', error);
            alert('모임 정보를 불러오는데 실패했습니다.');
            setModalOpen(false);
        } finally {
            setModalLoading(false);
        }
    };

    const handleTabChange = async (tab) => {
        setActiveTab(tab);

        if (tab === 'chat' && selectedMeeting) {
            try {
                const chatResponse = await getChatListOnMeeting(selectedMeeting.id);
                setChatList(chatResponse);
            } catch (error) {
                console.error('채팅 목록 조회 실패:', error);
                alert('채팅 목록을 불러오는데 실패했습니다.');
            }
        } else if (tab === 'image') {
            // 이미지 탭으로 이동할 때 이미지 URL 초기화
            setMeetingImageUrl(null);
        }
    };

    const handleLoadMeetingImage = async () => {
        if (!selectedMeeting || !selectedMeeting.imageId) {
            alert('이미지 ID가 없습니다.');
            return;
        }

        setImageLoading(true);
        try {
            const response = await getMeetingImageUrl(selectedMeeting.id);
            setMeetingImageUrl(response.imageUrl);
        } catch (error) {
            console.error('이미지 URL 조회 실패:', error);
            alert('이미지를 불러오는데 실패했습니다.');
        } finally {
            setImageLoading(false);
        }
    };

    const handleStatusChange = async () => {
        try {
            await changeMeetingStatus(selectedMeeting.id, selectedStatus);
            alert('모임 상태가 변경되었습니다.');
            // 선택된 모임 정보 업데이트
            setSelectedMeeting({...selectedMeeting, status: selectedStatus});
            // 목록 새로고침
            if (isSearchMode && searchQuery.trim()) {
                handleSearch(currentPage);
            } else {
                fetchMeetings(currentPage);
            }
        } catch (error) {
            console.error('모임 상태 변경 실패:', error);
            alert('모임 상태 변경에 실패했습니다.');
        }
    };

    const handleHardDelete = async () => {
        const confirmed = window.confirm(`정말로 "${selectedMeeting.title}" 모임을 데이터베이스에서 영구히 삭제하시겠습니까? 이 동작은 되돌릴 수 없습니다.`);
        if (!confirmed) return;

        try {
            await hardDeleteMeeting(selectedMeeting.id);
            alert('모임이 삭제되었습니다.');
            setModalOpen(false);
            // 목록 새로고침
            if (isSearchMode && searchQuery.trim()) {
                handleSearch(currentPage);
            } else {
                fetchMeetings(currentPage);
            }
        } catch (error) {
            console.error('모임 삭제 실패:', error);
            alert('모임 삭제에 실패했습니다.');
        }
    };

    useEffect(() => {
        fetchMeetings();
    }, []);

    const handlePageChange = (page) => {
        if (page >= 0 && page < totalPages) {
            setCurrentPage(page);
            if (isSearchMode && searchQuery.trim()) {
                handleSearch(page);
            } else {
                fetchMeetings(page);
            }
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleString('ko-KR');
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(0);
        handleSearch(0);
    };

    const handleReset = () => {
        setSearchQuery('');
        setCurrentPage(0);
        fetchMeetings(0);
    };

    // 테이블 컬럼 정의
    const columns = [
        { header: 'id', field: 'id', minWidth: 'min-w-12' },
        { header: 'hostId', field: 'hostId', minWidth: 'min-w-16' },
        { header: 'status', field: 'status', minWidth: 'min-w-20' },
        {
            header: 'imageId',
            field: 'imageId',
            minWidth: 'min-w-16',
            render: (meeting) => meeting.imageId || ''
        },
        { header: 'title', field: 'title', minWidth: 'min-w-32' },
        {
            header: 'content',
            field: 'content',
            minWidth: 'min-w-32',
            render: (meeting) => {
                if (!meeting.content) return '';
                return meeting.content.length > 30 ? `${meeting.content.substring(0, 30)}...` : meeting.content;
            }
        },
        {
            header: 'scheduledAt',
            field: 'scheduledAt',
            minWidth: 'min-w-32',
            render: (meeting) => formatDateTime(meeting.scheduledAt)
        },
        { header: 'placeName', field: 'placeName', minWidth: 'min-w-24' },
        { header: 'placeAddress', field: 'placeAddress', minWidth: 'min-w-32' },
        {
            header: 'placePhone',
            field: 'placePhone',
            minWidth: 'min-w-24',
            render: (meeting) => meeting.placePhone || ''
        },
        {
            header: 'placeLatitude',
            field: 'placeLatitude',
            minWidth: 'min-w-20',
            render: (meeting) => meeting.placeLatitude || ''
        },
        {
            header: 'placeLongitude',
            field: 'placeLongitude',
            minWidth: 'min-w-20',
            render: (meeting) => meeting.placeLongitude || ''
        },
        {
            header: 'partnerPlaceId',
            field: 'partnerPlaceId',
            minWidth: 'min-w-20',
            render: (meeting) => meeting.partnerPlaceId || ''
        },
        { header: 'minCapacity', field: 'minCapacity', minWidth: 'min-w-16' },
        { header: 'maxCapacity', field: 'maxCapacity', minWidth: 'min-w-16' },
        { header: 'memberCount', field: 'memberCount', minWidth: 'min-w-16' },
        {
            header: 'requiresApproval',
            field: 'requiresApproval',
            minWidth: 'min-w-20',
            render: (meeting) => meeting.requiresApproval ? 'true' : 'false'
        },
        {
            header: 'createdAt',
            field: 'createdAt',
            minWidth: 'min-w-32',
            render: (meeting) => formatDateTime(meeting.createdAt)
        },
        {
            header: 'updatedAt',
            field: 'updatedAt',
            minWidth: 'min-w-32',
            render: (meeting) => formatDateTime(meeting.updatedAt)
        }
    ];

    return (
        <DashboardLayout>
            <div className="p-3">
                <h1 className="text-lg font-bold mb-3">모임 관리</h1>

                {/* 검색 폼 */}
                <div className="mb-3 p-2 bg-gray-100 border border-gray-300">
                    <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 text-xs">
                        <div className="flex gap-2">
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="searchField"
                                    value="id"
                                    checked={searchField === 'id'}
                                    onChange={(e) => setSearchField(e.target.value)}
                                    className="mr-1"
                                />
                                ID
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="searchField"
                                    value="host_id"
                                    checked={searchField === 'host_id'}
                                    onChange={(e) => setSearchField(e.target.value)}
                                    className="mr-1"
                                />
                                호스트ID
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="searchField"
                                    value="title"
                                    checked={searchField === 'title'}
                                    onChange={(e) => setSearchField(e.target.value)}
                                    className="mr-1"
                                />
                                제목
                            </label>
                        </div>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="검색어 입력"
                            className="px-2 py-1 border border-gray-300 text-xs"
                        />
                        <button type="submit" className="px-2 py-1 bg-gray-200 border border-gray-300 hover:bg-gray-300 text-xs">
                            검색
                        </button>
                        <button type="button" onClick={handleReset} className="px-2 py-1 bg-gray-200 border border-gray-300 hover:bg-gray-300 text-xs">
                            전체보기
                        </button>
                    </form>
                </div>

                {/* 상단 정보 */}
                <div className="mb-2 text-xs text-gray-600">
                    총 {totalElements}개 | {currentPage + 1}/{totalPages} 페이지
                    {isSearchMode && ` | 검색: ${searchField}="${searchQuery}"`}
                </div>

                {/* 페이지네이션 테이블 */}
                <PaginatedTableContainer
                    columns={columns}
                    data={meetings}
                    loading={loading}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onRowClick={handleRowClick}
                />

                {/* 모임 상세 모달 */}
                <InfoModal
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    title={`모임 정보 - ${selectedMeeting?.title || ''}`}
                    width="w-[700px]"
                >
                    {modalLoading ? (
                        <div className="text-center py-4">모임 정보를 불러오는 중...</div>
                    ) : selectedMeeting ? (
                        <div className="text-xs">
                            {/* 윈도우 2000 스타일 탭 */}
                            <div className="mb-4">
                                <div className="flex border-b border-gray-400">
                                    <button
                                        onClick={() => handleTabChange('info')}
                                        className={`px-3 py-2 text-xs border-l border-t border-r border-gray-400 ${
                                            activeTab === 'info' 
                                                ? 'bg-white border-b-white -mb-px relative z-10' 
                                                : 'bg-gray-200 border-b-gray-400'
                                        }`}
                                    >
                                        모임 정보
                                    </button>
                                    <button
                                        onClick={() => handleTabChange('chat')}
                                        className={`px-3 py-2 text-xs border-t border-r border-gray-400 ${
                                            activeTab === 'chat' 
                                                ? 'bg-white border-b-white -mb-px relative z-10' 
                                                : 'bg-gray-200 border-b-gray-400'
                                        }`}
                                    >
                                        채팅 열람
                                    </button>
                                    <button
                                        onClick={() => handleTabChange('image')}
                                        className={`px-3 py-2 text-xs border-t border-r border-gray-400 ${
                                            activeTab === 'image' 
                                                ? 'bg-white border-b-white -mb-px relative z-10' 
                                                : 'bg-gray-200 border-b-gray-400'
                                        }`}
                                    >
                                        이미지 보기
                                    </button>
                                </div>
                            </div>

                            {/* 탭 내용 */}
                            {activeTab === 'info' ? (
                                <div className="max-h-96 overflow-y-auto space-y-4">
                                    {/* 모임 정보 테이블 */}
                                    <div>
                                        <h3 className="font-bold mb-2 text-sm">모임 정보</h3>
                                        <table className="w-full border border-gray-400">
                                            <tbody>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100 w-24">id</td><td className="border border-gray-400 px-2 py-1">{selectedMeeting.id}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">hostId</td><td className="border border-gray-400 px-2 py-1">{selectedMeeting.hostId}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">status</td><td className="border border-gray-400 px-2 py-1">{selectedMeeting.status}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">imageId</td><td className="border border-gray-400 px-2 py-1">{selectedMeeting.imageId || ''}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">title</td><td className="border border-gray-400 px-2 py-1">{selectedMeeting.title}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">content</td><td className="border border-gray-400 px-2 py-1">{selectedMeeting.content || ''}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">scheduledAt</td><td className="border border-gray-400 px-2 py-1">{formatDateTime(selectedMeeting.scheduledAt)}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">placeName</td><td className="border border-gray-400 px-2 py-1">{selectedMeeting.placeName}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">placeAddress</td><td className="border border-gray-400 px-2 py-1">{selectedMeeting.placeAddress}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">placePhone</td><td className="border border-gray-400 px-2 py-1">{selectedMeeting.placePhone || ''}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">placeLatitude</td><td className="border border-gray-400 px-2 py-1">{selectedMeeting.placeLatitude || ''}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">placeLongitude</td><td className="border border-gray-400 px-2 py-1">{selectedMeeting.placeLongitude || ''}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">partnerPlaceId</td><td className="border border-gray-400 px-2 py-1">{selectedMeeting.partnerPlaceId || ''}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">minCapacity</td><td className="border border-gray-400 px-2 py-1">{selectedMeeting.minCapacity}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">maxCapacity</td><td className="border border-gray-400 px-2 py-1">{selectedMeeting.maxCapacity}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">memberCount</td><td className="border border-gray-400 px-2 py-1">{selectedMeeting.memberCount}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">requiresApproval</td><td className="border border-gray-400 px-2 py-1">{selectedMeeting.requiresApproval ? 'true' : 'false'}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">createdAt</td><td className="border border-gray-400 px-2 py-1">{formatDateTime(selectedMeeting.createdAt)}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">updatedAt</td><td className="border border-gray-400 px-2 py-1">{formatDateTime(selectedMeeting.updatedAt)}</td></tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* 모임원 목록 */}
                                    <div>
                                        <h3 className="font-bold mb-2 text-sm">모임원 목록 ({meetingMembers.length}명)</h3>
                                        <table className="w-full border border-gray-400">
                                            <thead>
                                                <tr className="bg-gray-200">
                                                    <th className="border border-gray-400 px-2 py-1 text-left">id</th>
                                                    <th className="border border-gray-400 px-2 py-1 text-left">name</th>
                                                    <th className="border border-gray-400 px-2 py-1 text-left">imageUrl</th>
                                                    <th className="border border-gray-400 px-2 py-1 text-left">point</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {meetingMembers.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="5" className="border border-gray-400 py-2 text-center">
                                                            모임원이 없습니다.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    meetingMembers.map((member, index) => (
                                                        <tr key={index}>
                                                            <td className="border border-gray-400 px-2 py-1">{member.id}</td>
                                                            <td className="border border-gray-400 px-2 py-1">{member.name}</td>
                                                            <td className="border border-gray-400 px-2 py-1">{member.imageUrl || ''}</td>
                                                            <td className="border border-gray-400 px-2 py-1">{member.point || 0}</td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* 상태 변경 */}
                                    <div>
                                        <h3 className="font-bold mb-2 text-sm">상태 변경</h3>
                                        <div className="flex gap-2 items-center">
                                            <select
                                                value={selectedStatus}
                                                onChange={(e) => setSelectedStatus(e.target.value)}
                                                className="px-2 py-1 border border-gray-300 flex-1"
                                            >
                                                {statusOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                onClick={handleStatusChange}
                                                className="px-3 py-1 bg-gray-200 border border-gray-400 hover:bg-gray-300"
                                            >
                                                적용
                                            </button>
                                        </div>
                                    </div>

                                    {/* 하드 삭제 */}
                                    <div>
                                        <h3 className="font-bold mb-2 text-sm">Hard Delete</h3>
                                        <button
                                            onClick={handleHardDelete}
                                            className="px-3 py-1 bg-red-200 border border-gray-400 hover:bg-red-300 text-red-800"
                                        >
                                            모임 영구 삭제
                                        </button>
                                    </div>
                                </div>
                            ) : activeTab === 'chat' ? (
                                /* 채팅 열람 탭 */
                                <div>
                                    <h3 className="font-bold mb-2 text-sm">채팅 목록 ({chatList.length}개)</h3>
                                    <div className="border border-gray-400 bg-white max-h-96 overflow-y-auto">
                                        {chatList.length === 0 ? (
                                            <div className="p-4 text-center text-gray-500">
                                                채팅이 없습니다.
                                            </div>
                                        ) : (
                                            <div className="p-2 space-y-1">
                                                {chatList.map((chat, index) => (
                                                    <div key={index} className="border-b border-gray-200 pb-1 mb-1 last:border-b-0">
                                                        <div className="flex justify-between items-start">
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2 text-xs text-gray-600">
                                                                    <span>ID: {chat.memberId || 'SYSTEM'}</span>
                                                                    <span className="px-1 bg-gray-100 border border-gray-300">{chat.type}</span>
                                                                    <span>{formatDateTime(chat.createdAt)}</span>
                                                                </div>
                                                                <div className="mt-1 text-xs">
                                                                    {chat.content}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                /* 이미지 보기 탭 */
                                <div>
                                    <h3 className="font-bold mb-2 text-sm">모임 이미지</h3>
                                    <div className="border border-gray-400 p-3">
                                        <div className="mb-2">
                                            <button
                                                onClick={handleLoadMeetingImage}
                                                className="px-3 py-1 bg-gray-200 border border-gray-400 hover:bg-gray-300"
                                                disabled={!selectedMeeting?.imageId || imageLoading}
                                            >
                                                {imageLoading ? '로딩 중...' : '이미지 불러오기'}
                                            </button>
                                            {!selectedMeeting?.imageId && (
                                                <span className="ml-2 text-xs text-gray-500">이미지 ID가 없습니다.</span>
                                            )}
                                        </div>
                                        {meetingImageUrl && (
                                            <div className="mt-2">
                                                <img
                                                    src={meetingImageUrl}
                                                    alt="모임 이미지"
                                                    className="max-w-full max-h-96 border border-gray-300"
                                                    onError={() => {
                                                        alert('이미지를 불러올 수 없습니다.');
                                                        setMeetingImageUrl(null);
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : null}
                </InfoModal>
            </div>
        </DashboardLayout>
    );
};

export default MeetingManagementPage;
